import PropTypes from 'prop-types';
import { NewBookingDeliveryPropType } from 'common/prop-types';
import React, { useCallback, useEffect, useMemo } from 'react';
import { connect, useSelector } from 'react-redux';
import I18n from 'i18n-js';
import _get from 'lodash/get';
import _every from 'lodash/every';
import _isEmpty from 'lodash/isEmpty';
import _sumBy from 'lodash/sumBy';
import usePrevious from 'common/usePrevious';
import { Button, Popover, Switch } from 'grab-ui';
import {
  bulkEditOverlaySelector,
  bookingConfigurationSelector,
} from 'selectors/new-booking-selectors';
import {
  itemCategoriesSelector,
  featureFlagsSelector,
  enableMSDSelector,
} from 'selectors/app-selector';
import {
  bulkUploadGroupingFetchingQuoteSelector,
  bulkUploadGroupingIsGroupedSelector,
  bulkUploadMetaDataSelector,
} from 'selectors/new-bulk-upload-selectors';
import FloatingFooter from 'components/floating-footer';
import formatCurrency from 'common/utils/format-currency';
import { deliveryPriceReady } from 'common/anytime-booking/delivery';
import calcPrice from 'common/utils/calc-price';
import scribeNewBulkUploadDuck from 'ducks/scribe-new-bulk-upload-duck';
import cx from 'classnames';
import excelSvg from 'public/icons/bulkupload/excel.svg';
import { downloadOptimizedResult } from 'components/v2/anytime-booking/bulk-uploader/new-bulk-upload-generator';

import styles from './footer.less';

const BulkBookingFooter = ({
  dispatch,
  deliveries,
  bulkEditOverlay,
  selectedDeliveryIDs,
  scribeSetupDeliveriesCommonData,
  onChangeAutoMultiStop,
  disabledAutoGroup,
  bulkUploadGroupingFetchingQuote,
  bulkUploadGroupingIsGrouped,
  stepForwardFunc,
  enableMSD,
}) => {
  const prevBulkUploadGroupingIsGrouped = usePrevious(
    bulkUploadGroupingIsGrouped,
  );
  const deliveriesWithPriceReady = useMemo(
    () => deliveries.filter(deliveryPriceReady),
    [deliveries],
  );

  const metaDataWithVersion = useSelector(bulkUploadMetaDataSelector);
  const itemCategories = useSelector(itemCategoriesSelector);

  const bookingConfiguration = useSelector(bookingConfigurationSelector);

  const featureFlags = useSelector(featureFlagsSelector);

  const totalPrice = useMemo(
    () => {
      if (_isEmpty(deliveriesWithPriceReady)) {
        return undefined;
      }
      const currency = _get(deliveriesWithPriceReady[0], 'quote.currency');
      const totalAmount = _sumBy(deliveriesWithPriceReady, delivery =>
        _get(delivery, 'quote.originalFare.lowerBound', 0),
      );
      return {
        currencyCode: _get(currency, 'code'),
        amount: calcPrice(totalAmount, currency),
      };
    },
    [deliveriesWithPriceReady],
  );

  const totalPriceDisplay = useMemo(
    () => {
      if (!totalPrice) {
        return '--';
      }
      return formatCurrency({
        code: _get(totalPrice, 'currencyCode'),
        amount: _get(totalPrice, 'amount'),
      });
    },
    [totalPrice],
  );

  const paymentButtonDisabled = useMemo(
    () =>
      !_isEmpty(selectedDeliveryIDs) ||
      _isEmpty(deliveries) ||
      !_every(deliveries, deliveryPriceReady) ||
      bulkUploadGroupingFetchingQuote,
    [deliveries, selectedDeliveryIDs, bulkUploadGroupingFetchingQuote],
  );

  const onStepForwardFunc = useCallback(
    () => {
      stepForwardFunc();
      dispatch(
        scribeNewBulkUploadDuck.creators.scribeExpressBukUploadSetupDeliveryConfirm(
          scribeSetupDeliveriesCommonData,
          totalPrice,
        ),
      );
    },
    [dispatch, stepForwardFunc, scribeSetupDeliveriesCommonData, totalPrice],
  );

  const goToPaymentsButtonWithPopover = useMemo(
    () => {
      const goToPaymentsButton = (
        <div className={styles.buttonsWrapper}>
          <Button
            type={'primary'}
            disabled={paymentButtonDisabled}
            onClick={onStepForwardFunc}
            className={styles.paymentButton}
          >
            {I18n.t(
              'phrase.geWebBulkUpload.express_web_bulk_upload_button_go_to_payments_happypath',
            )}
          </Button>
        </div>
      );

      return paymentButtonDisabled ? (
        <Popover
          trigger={'hover'}
          placement={'top'}
          content={I18n.t(
            'phrase.geWebBulkUpload.express_web_bulk_upload_body_delivery_option_for_all_error',
          )}
          overlayClassName={styles.buttonPopover}
        >
          {goToPaymentsButton}
        </Popover>
      ) : (
        goToPaymentsButton
      );
    },
    [paymentButtonDisabled, onStepForwardFunc],
  );

  useEffect(
    () => {
      if (!disabledAutoGroup) {
        if (
          (prevBulkUploadGroupingIsGrouped === true &&
            bulkUploadGroupingIsGrouped === false) ||
          (prevBulkUploadGroupingIsGrouped === false &&
            bulkUploadGroupingIsGrouped === true)
        ) {
          dispatch(
            scribeNewBulkUploadDuck.creators.scribeExpressBulkUploadSetupDeliveryToggleMultiStop(
              bulkUploadGroupingIsGrouped,
            ),
          );
        }
      }
    },
    [
      disabledAutoGroup,
      bulkUploadGroupingIsGrouped,
      prevBulkUploadGroupingIsGrouped,
    ],
  );

  const onExportOptimizedResult = useCallback(
    () => {
      downloadOptimizedResult({
        deliveries,
        metaDataWithVersion,
        itemCategories,
        bookingConfiguration,
        featureFlags,
      });
    },
    [
      deliveries,
      metaDataWithVersion,
      itemCategories,
      bookingConfiguration,
      featureFlags,
    ],
  );

  const exportButtonRender = useMemo(
    () => {
      if (disabledAutoGroup || !bulkUploadGroupingIsGrouped) {
        return null;
      }

      return (
        <Button
          className={cx(styles.exportButton)}
          onClick={onExportOptimizedResult}
          id={'exportOptimizedXLSButton'}
        >
          <img className={cx(styles.excelIcon)} src={excelSvg} alt="" />
          {I18n.t('bulkUploadRevamp.step3.exportOptimizedResult')}
        </Button>
      );
    },
    [disabledAutoGroup, bulkUploadGroupingIsGrouped, onExportOptimizedResult],
  );

  const autoGroupSwitcherWithPopover = useMemo(
    () => {
      const switcher = (
        <Switch
          disabled={disabledAutoGroup}
          checked={!disabledAutoGroup && bulkUploadGroupingIsGrouped}
          onChange={onChangeAutoMultiStop}
        />
      );

      return (
        <span className={styles.autoGroupSwitcherWrapper}>
          <span className={styles.autoGroupSwitcher}>
            {disabledAutoGroup ? (
              <Popover
                trigger={'hover'}
                placement={'top'}
                content={I18n.t(
                  'phrase.geWebBulkUpload.express_web_route_optimisation_tooltip_change_delivery_type',
                )}
                overlayClassName={styles.buttonPopover}
              >
                {switcher}
              </Popover>
            ) : (
              switcher
            )}
          </span>
          {I18n.t('bulkUploadRevamp.step3.grouping.text')}
        </span>
      );
    },
    [disabledAutoGroup, onChangeAutoMultiStop, bulkUploadGroupingIsGrouped],
  );

  return (
    <FloatingFooter
      visible={!_get(bulkEditOverlay, 'visible')}
      contentWrapperClassName={styles.footer}
    >
      <div className={styles.autoGroupWrapper}>
        {enableMSD && autoGroupSwitcherWithPopover}
        {enableMSD && exportButtonRender}
      </div>
      <span className={styles.totalPriceLabelGroup}>
        <span className={styles.totalPriceLabel}>
          {I18n.t(
            'phrase.geWebBulkUpload.express_web_bulk_upload_body_total_happypath',
          )}
        </span>
        <span className={styles.totalPrice}> {totalPriceDisplay} </span>
        {goToPaymentsButtonWithPopover}
      </span>
    </FloatingFooter>
  );
};

BulkBookingFooter.propTypes = {
  bulkEditOverlay: PropTypes.shape({}).isRequired,
  deliveries: PropTypes.arrayOf(NewBookingDeliveryPropType).isRequired,
  selectedDeliveryIDs: PropTypes.arrayOf(PropTypes.string).isRequired,
  scribeSetupDeliveriesCommonData: PropTypes.shape({}).isRequired,
  bulkUploadGroupingFetchingQuote: PropTypes.bool.isRequired,
  bulkUploadGroupingIsGrouped: PropTypes.bool.isRequired,
  onChangeAutoMultiStop: PropTypes.func.isRequired,
  disabledAutoGroup: PropTypes.bool.isRequired,
  stepForwardFunc: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  enableMSD: PropTypes.bool.isRequired,
};

export default connect(state => ({
  bulkEditOverlay: bulkEditOverlaySelector(state),
  bulkUploadGroupingFetchingQuote: bulkUploadGroupingFetchingQuoteSelector(
    state,
  ),
  bulkUploadGroupingIsGrouped: bulkUploadGroupingIsGroupedSelector(state),
  enableMSD: enableMSDSelector(state),
}))(BulkBookingFooter);
